<!--
  The SkipToMainContent component is used to skip to the main content of the page.
  - It is a banner that is hidden by default and becomes visible when focused.
  - When clicked, it blurs the banner, focuses on the main content, and scrolls to the main content.
  - The height of the SkipToMainContent component is used to calculate the height of the top banner.
  - The height of the SkipToMainContent component is set to 36px when focused and 0px when not focused.
  - The height change event is emitted to the parent component when the height of the SkipToMainContent component changes.
-->
<template>
  <a
    id="skipToMainContent"
    ref="skipToMainContent"
    href="javascript:void(0)"
    class="skip-to-main-content"
    :class="{ 'stmc-home': props.isHeaderScrollable }"
    :aria-label="$t('SHARED.SKIP_TO_MAIN_CONTENT')"
    @click="skipToContent"
    @focus="emitHeightChange(true)"
    @blur="emitHeightChange()"
  >
    {{ $t('SHARED.SKIP_TO_MAIN_CONTENT') }}</a
  >
</template>

<script setup>
/**
 * Props
 * - hdrHeight: Height of the header
 */
const props = defineProps({
  hdrHeight: {
    type: Number,
    required: true
  },
  isHeaderScrollable: {
    type: Boolean,
    default: false
  }
});
/**
 * Reactive variables
 * - skipToMainContent: Ref to the skipToMainContent element
 */
const skipToMainContent = shallowRef(null);

/**
 * Emit the height change event to the parent component
 */
const emit = defineEmits(['heightChange']);

const emitHeightChange = (stmcVisible = false) => {
  if (stmcVisible) {
    emit('heightChange', 'skipToMainContent', 36);
  } else {
    emit('heightChange', 'skipToMainContent', 0);
  }
};

/**
 * Skip to the main content implementation
 * - Blurs the "Skip to main content" banner
 * - Focuses on the main content
 * - Scrolls to the main content
 */
const skipToContent = async () => {
  skipToMainContent.value.blur(); //This is done because some browsers (Safari Sonoma14) do not remove the focus from the element when clicked
  await nextTick(); // Wait for the DOM to update --top-banner-height variable
  const mainContent = document.getElementsByClassName('main-content')[0];
  mainContent.focus({ preventScroll: true });
  window.scrollTo({
    top:
      mainContent.offsetTop -
      document.documentElement.style
        .getPropertyValue('--top-banner-height')
        .replace('px', '') -
      props.hdrHeight -
      (document.documentElement.style
        .getPropertyValue('--top-banner-height')
        .replace('px', '') == 0 && props.isHeaderScrollable
        ? 36
        : 8),
    behavior: 'instant'
  });
};
</script>

<style lang="scss" scoped>
.skip-to-main-content {
  text-align: center;
  width: 0;
  height: 0;
  position: absolute;
  overflow: hidden;
  background-color: transparent;
}

.skip-to-main-content:focus {
  position: unset;
  width: 100%;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--cup-secondary-color-1);
  font-size: 14px;
  line-height: 20px;
  text-decoration: underline;
  padding: 8px 16px;
  border: 3px solid var(--cup-general-color-5);
  vertical-align: middle;
  color: var(--cup-general-color-5);
  outline: none !important;
}
.stmc-home:focus {
  position: relative;
  margin-bottom: -3px !important;
  width: 100vw !important;
  box-shadow: none !important;
}
</style>
